/**
 * Coarse device shape, attached to checkout events.
 *
 * The open question about the checkout drop-off is whether people abandon or
 * the form breaks, and the card modal has been suspected on mobile before. That
 * question is only answerable if completion can be split by device — a single
 * blended rate cannot distinguish "half our buyers give up" from "it does not
 * work on the phones 99% of our traffic uses".
 *
 * Deliberately COARSE. A viewport bucket and whether the thing has a touch
 * screen are enough to compare completion across device classes; a full user
 * agent or exact pixel size would be a fingerprinting surface on an analytics
 * row that outlives the session, for no extra answer.
 */

import { useSyncExternalStore } from 'react';

/**
 * ONE query string for both readers below. `deviceContext()` stamps it on
 * events, `useCoarsePointer()` lets the checkout lay itself out by it — if the
 * two ever disagreed, the analytics dimension would stop describing the UI it
 * is meant to explain.
 */
const COARSE_POINTER_QUERY = '(pointer: coarse)';

export interface DeviceContext {
  /** Viewport bucket, not exact width — see the file comment. */
  device: 'mobile' | 'tablet' | 'desktop';
  /** True when the primary input is a touch screen. */
  touch: boolean;
}

/** Matches the app's mobile-first Tailwind breakpoints closely enough. */
const TABLET_MIN = 640;
const DESKTOP_MIN = 1024;

export function deviceContext(): DeviceContext {
  // SSR/prerender: no window. Report desktop rather than guessing — these
  // events only ever fire from a real click, so this branch is unreachable in
  // practice and exists so the helper is safe to import anywhere.
  if (typeof window === 'undefined') return { device: 'desktop', touch: false };

  const width = window.innerWidth || 0;
  const device = width >= DESKTOP_MIN ? 'desktop' : width >= TABLET_MIN ? 'tablet' : 'mobile';

  let touch = false;
  try {
    touch = window.matchMedia(COARSE_POINTER_QUERY).matches;
  } catch {
    // Older browsers without matchMedia — absence is not evidence of a mouse,
    // but `device` still carries the useful half.
  }

  return { device, touch };
}

/*
 * ── IS THE PRIMARY INPUT A TOUCH SCREEN? ─────────────────────────────────
 *
 * The render-time twin of `deviceContext().touch`, for the places where the
 * page lays itself out by it rather than merely reporting it. First use: the
 * one-time sheet on /plans puts Click before Payme on a touch screen (owner,
 * 2026-09-25).
 *
 * Hydration-safe by construction: `/plans` is prerendered, and the server
 * snapshot is `false`, so the first client render reproduces the server
 * markup (Payme first) and React re-renders with the real answer straight
 * after. Reading `matchMedia` inside a `useState` initialiser instead would
 * hand a phone a Click tile carrying Payme's server-rendered logo — React
 * does not patch attribute mismatches on hydration, it only warns.
 *
 * Booleans are stable snapshots, so no memoisation is needed for
 * `useSyncExternalStore`'s tearing check.
 */
function canMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function readCoarsePointer(): boolean {
  if (!canMatchMedia()) return false;
  try {
    return window.matchMedia(COARSE_POINTER_QUERY).matches;
  } catch {
    return false;
  }
}

function subscribeCoarsePointer(onChange: () => void): () => void {
  if (!canMatchMedia()) return () => {};
  try {
    const mql = window.matchMedia(COARSE_POINTER_QUERY);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  } catch {
    return () => {};
  }
}

const serverSnapshot = () => false;

/** True when the primary input is a touch screen; `false` on the server and in browsers without `matchMedia`. */
export function useCoarsePointer(): boolean {
  return useSyncExternalStore(subscribeCoarsePointer, readCoarsePointer, serverSnapshot);
}
